// slice-3574 (BigList #26) — the ONE place the frontend knows how a lookup option label is
// punctuated.
//
// `/api/lookups/*` returns `{ value, label }` where the label is built by
// `LookupService.fmt(code, description)` (backend .../platform/lookups/LookupService.java) as
// `CODE + OPTION_SEPARATOR + Description`. Six screens do not just RENDER that label — they take
// it apart again, because PB's `itemchanged` fills a read-only Description field from the row the
// operator picked and PH10 has the description only inside the label:
//
//   features/truck/dispatch/TruckDispatchPage.tsx        carrier_description readback
//   features/truck/dispatch/truckGridColumns.ts          same, in the grid editor
//   features/orders/fastpick-ship/OrderFastpickShipPage.tsx  ship_method description
//   features/warehouse/seasonassign/seasonAssignModel.ts season_class description
//   features/receipts/create/ReceiptCreatePage.tsx       PB shows the description ALONE
//   features/receipts/returnscan/useReturnsTote.ts       company half of "COMPANY - KEY"
//
// Every one of them used to hard-code the separator, so changing it in `fmt()` silently turned
// six Description fields blank with nothing failing. They all import from here now: one constant,
// and a change on the backend is a one-line change here rather than a scavenger hunt.
//
// The separator itself is PB's: `+ ' - ' +` appears 463 times across the 5,949 exported
// .srw/.sru/.srd files, an em-dash 4 times (never as a dropdown label), and PB's literal
// combined-string `ddlb values=` lists are hyphens too ("Y - Calc Required/Y",
// dw_cartlook_dets.srd). Keep it in step with `LookupService.OPTION_SEPARATOR`.

/** Exactly `LookupService.OPTION_SEPARATOR`. */
export const LOOKUP_LABEL_SEPARATOR = ' - ';

/** The separator PH10 emitted before slice-3574 (em dash). Still ACCEPTED when reading a label —
 *  a label cached client-side or a fixture written against the old format must not blank a
 *  Description field — but never emitted. */
const LEGACY_LOOKUP_LABEL_SEPARATOR = ' — ';

/** Index + length of the first separator in `label`, current format first, legacy second. */
function findSeparator(label: string): { i: number; len: number } {
  const i = label.indexOf(LOOKUP_LABEL_SEPARATOR);
  if (i >= 0) return { i, len: LOOKUP_LABEL_SEPARATOR.length };
  const j = label.indexOf(LEGACY_LOOKUP_LABEL_SEPARATOR);
  return { i: j, len: LEGACY_LOOKUP_LABEL_SEPARATOR.length };
}

/**
 * The description half of a `"CODE - Description"` lookup label — `''` for a bare-code label.
 *
 * <p>Splits on the FIRST separator: a description may legitimately contain " - " (PB's item-class
 * dddw label is `class - owner - description`), and only the code is fixed-width.
 */
export function descriptionFromLookupLabel(label: string | undefined | null): string {
  if (!label) return '';
  const { i, len } = findSeparator(label);
  return i < 0 ? '' : label.slice(i + len).trim();
}

/**
 * The code half of a `"CODE - Description"` lookup label — the whole label when there is no
 * separator.
 */
export function codeFromLookupLabel(label: string | undefined | null): string {
  if (!label) return '';
  const { i } = findSeparator(label);
  return (i < 0 ? label : label.slice(0, i)).trim();
}
